import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@apollo/client";
import {  useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Checkbox, FormGroup, Input, Textarea } from "@cofano/cck";

import NumberInput from "components/cck/NumberInput";
import CountrySelect from "components/selects/CountrySelect";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Fields, hasOptionalFieldPermission, Language } from "imports/Utils";

import {
	hasAdnCargoTankDesignPermission,
	hasAdnCargoTankTypePermission,
	hasAdnShipTypePermission,
	hasBobbyCatPermission,
	hasBoilerPermission,
	hasCapacityTeuPermission,
	hasCellGuidesPermission,
	hasClassificationSocietyPermission,
	hasDeckLoadPermission,
	hasDroplinePermission,
	hasEniPermission,
	hasImoPermission,
	hasInsurerPermission,
	hasLicensePlatePermission,
	hasMaxCranesOnQuaysPermission,
	hasMemoPermission,
	hasNiwoPermission,
	hasNumberOfCranesPermission,
	hasNumberOfTanksPermission,
	hasPumpCapacityPermission,
	hasPushBowPermission,
	hasReeferConnectionPermission,
	hasSailersPermission,
	hasShuttersPermission,
	hasShuttersTypePermission,
	hasSpillRailPermission,
	hasTankCladdingTypePermission,
	hasTracksPermission,
	hasTrussingPermission,
} from "view/ships/utils/ShipUtils";
import AdnCargoTankTypeSelect from "view/ships/components/selects/AdnCargoTankTypeSelect";
import AdnShipTypeSelect from "view/ships/components/selects/AdnShipTypeSelect";
import AdnCargoTankDesignSelect from "view/ships/components/selects/AdnCargoTankDesignSelect";
import ShipBargeTypeSelect from "view/ships/components/selects/ShipBargeTypeSelect";
import ShipFuelTypeSelect from "view/ships/components/selects/ShipFuelTypeSelect";
import ShipVesselTypeSelect from "view/ships/components/selects/ShipVesselTypeSelect";
import ShipCemtClassificationTypeSelect from "view/ships/components/selects/ShipCemtClassificationTypeSelect";
import ShipTankCladdingTypeSelect from "view/ships/components/selects/ShipTankCladdingTypeSelect";
import ShipTrackTraceProviderSelect from "view/ships/components/selects/ShipTrackTraceProviderSelect";
import { ShipSchemaInput, ShipSchemaOutput } from "view/ships/utils/ship.schema";
import SailingScheduleSelect, { SailingScheduleSelectOption } from "view/sailingschedules/components/selects/SailingScheduleSelect";

import { GetSailingSchedulesDocument } from "__generated__/view/sailingschedules/graphql/queries.generated";
import { BargeType, VesselType } from "__generated__/enums";

const ShipDetailsForm = () => {
	const session = useStoreState(store => store.session);

	const { control, watch } = useFormContext<ShipSchemaInput, unknown, ShipSchemaOutput>();

	const ship = watch();

	const editMode = !!ship.id;

	const { data } = useQuery(GetSailingSchedulesDocument);

	const sailingSchedules = (data?.sailingSchedules ?? []).filter((s): s is SailingScheduleSelectOption => s !== null);

	return (
		<React.Fragment>
			<FormGroup
				label={Language("SHIP_ADD_NAME")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="name"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} disabled={editMode} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("SHIP_ADD_VESSEL_TYPE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="vesselType"
					control={control}
					render={({ field }) => <ShipVesselTypeSelect {...field} clearable={false} />} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.SHIP, "abbreviation") && (
				<FormGroup
					label={Language("SHIPS_ABBREVIATION")}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="abbreviation"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "isDummy") && (
				<FormGroup label={Language("SHIP_DUMMY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isDummy"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasImoPermission(session, ship) && (
				<FormGroup
					label={Language("SHIP_ADD_IMO")}
					labelPlacement="left-center"
					overflow="hidden"
					required={ship.vesselType === VesselType.SeaShip}>
					<Controller
						name="imo"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} thousandSeparator={false} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasEniPermission(session, ship) && (
				<FormGroup
					label={Language("SHIP_ADD_ENI")}
					labelPlacement="left-center"
					overflow="hidden"
					required={ship.vesselType === VesselType.Barge}>
					<Controller
						name="eni"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "mmsi") && (
				<FormGroup label={Language("SHIPS_MMSI")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="mmsi"
						control={control}
						render={({ field }) => <NumberInput {...field} decimalScale={0} thousandSeparator={false} />} />
				</FormGroup>
			)}

			{hasLicensePlatePermission(session, ship) && (
				<FormGroup label={Language("SHIPS_LICENSE_PLATE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="licensePlate"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "gmp") && (
				<FormGroup label={Language("SHIP_GMP")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="gmp"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasNiwoPermission(session, ship) && (
				<FormGroup label={Language("SHIP_NIWO")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="niwo"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "cemtClassificationType") && (
				<FormGroup label={Language("SHIP_CEMT_CLASSIFICATION_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="cemtClassificationType"
						control={control}
						render={({ field }) => <ShipCemtClassificationTypeSelect {...field} />}
					/>
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "yearOfBuild") && (
				<FormGroup label={Language("SHIPS_YEAR_OF_BUILD")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="yearOfBuild"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} thousandSeparator={false} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "flag") && (
				<FormGroup label={Language("SHIP_FLAG")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="flag"
						control={control}
						render={({ field }) => <CountrySelect {...field} clearable={false} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "bargeType") && (
				<FormGroup label={Language("BARGE_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="bargeType"
						control={control}
						render={({ field }) => (
							<ShipBargeTypeSelect
								{...field}
								disabled={!!((field.value === BargeType.ContainerShip) && (ship.bayPlan?.properties || ship.bayPlan?.rows))} />
						)} />
				</FormGroup>
			)}

			{hasClassificationSocietyPermission(session, ship) && (
				<FormGroup label={Language("SHIP_CLASSIFICATION_SOCIETY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="classificationSociety"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasInsurerPermission(session) && (
				<FormGroup label={Language("SHIPS_INSURER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="insurer"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasAdnShipTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADN_SHIP_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="adnProperties.shipType"
						control={control}
						render={({ field }) => <AdnShipTypeSelect {...field} />} />
				</FormGroup>
			)}

			{hasAdnCargoTankDesignPermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADN_CARGO_TANK_DESIGN")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="adnProperties.cargoTankDesign"
						control={control}
						render={({ field }) => <AdnCargoTankDesignSelect {...field} />} />
				</FormGroup>
			)}

			{hasAdnCargoTankTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADN_CARGO_TANK_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="adnProperties.cargoTankType"
						control={control}
						render={({ field }) => <AdnCargoTankTypeSelect {...field} />} />
				</FormGroup>
			)}

			{hasTankCladdingTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_TANK_CLADDING_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="tankCladdingType"
						control={control}
						render={({ field }) => <ShipTankCladdingTypeSelect {...field} />} />
				</FormGroup>
			)}

			{ship.bargeType !== BargeType.PusherTug && hasOptionalFieldPermission(session, Fields.SHIP, "capacityTon") && (
				<FormGroup
					label={Language("SHIPS_CAPACITY_TON")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="capacityTon"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={2} addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{ship.bargeType !== BargeType.PusherTug && hasOptionalFieldPermission(session, Fields.SHIP, "capacityM3") && (
				<FormGroup
					label={Language("SHIPS_CAPACITY_M3")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="capacityM3"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={2} addonAfter={intl.formatMessage({ id: "UNIT_M3" })} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasCapacityTeuPermission(session, ship) && (
				<FormGroup
					label={Language("CAPACITY_TEU")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="capacityTeu"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={2} addonAfter={intl.formatMessage({ id: "UNIT_TEU" })} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasTracksPermission(session, ship) && (
				<FormGroup label={Language("TRACKS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="tracks"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} addonAfter="cm" />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "dimensions") && (
				<React.Fragment>
					<FormGroup label={Language("SHIPS_LENGTH")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="lengthCm"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<NumberInput {...field} decimalScale={2} addonAfter="m" />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={Language("SHIPS_WIDTH")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="widthCm"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<NumberInput {...field} decimalScale={2} addonAfter="m" />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={Language("SHIPS_MIN_DRAUGHT")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="minDraughtCm"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<NumberInput {...field} decimalScale={2} addonAfter="m" />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={Language("SHIPS_MAX_DRAUGHT")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="maxDraughtCm"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<NumberInput {...field} decimalScale={2} addonAfter="m" />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					{hasPumpCapacityPermission(session, ship) && (
						<FormGroup label={Language("SHIP_PUMP_CAPACITY")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="pumpCapacity"
								control={control}
								render={({ field }) => <NumberInput {...field} addonAfter="m³/h" />} />
						</FormGroup>
					)}

					{hasNumberOfTanksPermission(session, ship) && (
						<FormGroup label={Language("SHIP_NUMBER_OF_TANKS")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="numberOfTanks"
								control={control}
								render={({ field }) => <NumberInput {...field} decimalScale={0} />} />
						</FormGroup>
					)}
				</React.Fragment>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "fuelType") && (
				<FormGroup label={Language("SHIPS_FUEL_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="fuelType"
						control={control}
						render={({ field }) => <ShipFuelTypeSelect {...field} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgVelocityEmptyKmH") && (
				<FormGroup label={Language("SHIPS_AVG_VELOCITY_EMPTY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="avgVelocityEmptyKmH"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={2} addonAfter={"km/h"} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgVelocityFullKmH") && (
				<FormGroup label={Language("SHIPS_AVG_VELOCITY_FULL")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="avgVelocityFullKmH"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={2} addonAfter={"km/h"} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgFuelConsumptionLtrH") && (
				<FormGroup label={Language("SHIPS_AVG_FUEL_CONSUMPTION_HOUR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="avgFuelConsumptionLtrH"
						control={control}
						render={({ field }) => <NumberInput {...field} decimalScale={2} addonAfter={"ltr/h"} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgFuelConsumptionLtrKm") && (
				<FormGroup label={Language("SHIPS_AVG_FUEL_CONSUMPTION_KM")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="avgFuelConsumptionLtrKm"
						control={control}
						render={({ field }) => <NumberInput {...field} decimalScale={2} addonAfter={"ltr/km"} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "yearLastMaintenance") && (
				<FormGroup label={Language("SHIPS_YEAR_LAST_MAINTENANCE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="yearLastMaintenance"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} thousandSeparator={false} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasBobbyCatPermission(session, ship) && (
				<FormGroup label={Language("SHIPS_BOBBY_CAT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="bobbyCat"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasSailersPermission(session, ship) && (
				<FormGroup label={Language("SHIPS_SAILORS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="sailors"
						control={control}
						render={({ field }) => <NumberInput {...field} decimalScale={0} />} />
				</FormGroup>
			)}

			{hasReeferConnectionPermission(session, ship) && (
				<FormGroup label={Language("SHIPS_NUM_REEFER_CONNECTIONS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="numReeferConnections"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasDroplinePermission(session, ship) && (
				<FormGroup label={Language("SHIPS_DROPLINE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="dropline"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasTrussingPermission(session, ship) && (
				<FormGroup label={Language("SHIP_TRUSSING")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="trussing"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasPushBowPermission(session, ship) && (
				<FormGroup label={Language("PUSH_BOW")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="hasPushBow"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "pushingCertificate") && (
				<FormGroup label={Language("PUSHING_CERTIFICATE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="hasPushingCertificate"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasCellGuidesPermission(session, ship) && (
				<FormGroup label={Language("SHIP_CELL_GUIDES")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="cellGuides"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasShuttersPermission(session, ship) && (
				<FormGroup label={Language("SHIP_HAS_SHUTTERS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="hasShutters"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasShuttersTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_SHUTTERS_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="shuttersType"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasDeckLoadPermission(session, ship) && (
				<FormGroup label={Language("SHIP_DECK_LOAD")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="deckLoad"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={1} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgSpeedUpstreamKmH") && (
				<FormGroup label={Language("SHIP_AVG_SPEED_UPSTREAM_KMH")} labelPlacement="left-center" overflow="visible">
					<Controller
						name="avgSpeedUpstreamKmH"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={1} addonAfter="km/h" />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgSpeedDownstreamKmH") && (
				<FormGroup label={Language("SHIP_AVG_SPEED_DOWNSTREAM_KMH")} labelPlacement="left-center" overflow="visible">
					<Controller
						name="avgSpeedDownstreamKmH"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={1} addonAfter="km/h" />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasNumberOfCranesPermission(session, ship) && (
				<FormGroup label={Language("SHIP_NUMBER_OF_CRANES")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="numberOfCranes"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasMaxCranesOnQuaysPermission(session, ship) && (
				<FormGroup label={Language("SHIP_MAX_CRANES_ON_QUAY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="maxCranesOnQuay"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "trackerId") && (
				<FormGroup label={Language("SHIPS_TRACKER_ID")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="trackerId"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} thousandSeparator={false} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "trackTraceProvider") && (
				<FormGroup label={Language("SHIPS_TRACK_TRACE_PROVIDER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="trackTraceProvider"
						control={control}
						render={({ field }) => <ShipTrackTraceProviderSelect {...field} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "maxTonBallast") && (
				<FormGroup label={Language("SHIPS_MAX_TON_BALLAST")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="maxTonBallast"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={2} addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "airDraft") && (
				<FormGroup label={Language("SHIPS_AIR_DRAIFT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="airDraft"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={2} addonAfter="m" />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "corridor") && ship.bargeType !== BargeType.Tanker && (
				<FormGroup label={Language("SHIPS_CORRIDOR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="corridor"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "tankEye") && (
				<FormGroup label={Language("TANK_EYE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="tankEye"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "hasHeatingInstallation") && (
				<FormGroup label={Language("HAS_HEATING_INSTALLATION")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="hasHeatingInstallation"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasBoilerPermission(session, ship) && (
				<FormGroup label={Language("SHIP_BOILER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="boiler"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasSpillRailPermission(session, ship) && (
				<FormGroup label={Language("SHIP_SPILL_RAIL")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="spillRail"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{ship.bargeType === BargeType.PushedBarge && (
				<FormGroup label={Language("SELF_PROPELLED")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="selfPropelled"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "sailingSchedule") && (
				<FormGroup label={Language("VOYAGE_SCHEDULE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="sailingSchedule"
						control={control}
						render={({ field }) => (
							<SailingScheduleSelect
								value={sailingSchedules.find(s => s.id === field.value) ?? null}
								onChange={(val) => field.onChange(val?.id ?? null)}
							/>
						)}
					/>
				</FormGroup>
			)}

			{hasMemoPermission(session) && (
				<FormGroup label={Language("SHIPS_MEMO")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="memo"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Textarea {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasMemoPermission(session) && (
				<FormGroup label={Language("SHIP_PLANNING_NOTES")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="planningNotes"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Textarea {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}
		</React.Fragment>
	);
};

export default ShipDetailsForm;
